import { useState, useMemo, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { CalendarRange, Check, X, AlertCircle, Send, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DayEntry, AbsenceType, CreateAbsenceRequestData } from '../../types';
import {
  generateVacationRange,
  getBookableDays,
  createVacationEntries,
  removeVacationFromRange,
  countRemovableDays,
  calculateRangeSummary,
} from '../../utils/vacationRange';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

type Mode = 'add' | 'remove' | 'plan';

interface VacationRangeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Vorausgewähltes Startdatum (aus DayEditModal) */
  initialDate?: string;
  entries: Record<string, DayEntry>;
  /** Array von ISO-Datumsstrings der Feiertage */
  holidays: string[];
  year: number;
  /** Callback mit den aktualisierten Entries */
  onSave: (updatedEntries: Record<string, DayEntry>) => void;
  /** Anzeige "Urlaub für [Name]" (HR/Manager) */
  employeeName?: string;
  /** Verbleibende Urlaubstage vor der Buchung */
  remainingVacation?: number;
  /** Gesamtanspruch inkl. Übertrag */
  totalEntitlement?: number;
  /** Urlaubswirksame Abwesenheitsarten (für Typ-Dropdown) */
  vacationTypes?: AbsenceType[];
  /** Callback für Anträge (wenn Typ requiresApproval hat) */
  onSubmitRequest?: (data: CreateAbsenceRequestData) => Promise<void>;
  /** Alle planbaren Abwesenheitsarten (für Vormerken-Modus). Fällt auf vacationTypes zurück. */
  plannableTypes?: AbsenceType[];
  /** Callback für Vormerken (PLANNED-Status). Aktiviert den Vormerken-Modus. */
  onPlanRequest?: (data: CreateAbsenceRequestData) => Promise<void>;
}

export function VacationRangeDialog({
  open,
  onOpenChange,
  initialDate,
  entries,
  holidays,
  year,
  onSave,
  employeeName,
  remainingVacation,
  totalEntitlement,
  vacationTypes = [],
  onSubmitRequest,
  plannableTypes,
  onPlanRequest,
}: VacationRangeDialogProps) {
  const planEnabled = !!onPlanRequest;
  const typesForPlan = plannableTypes && plannableTypes.length > 0 ? plannableTypes : vacationTypes;
  const [mode, setMode] = useState<Mode>('add');
  const [startDate, setStartDate] = useState(initialDate || '');
  const [endDate, setEndDate] = useState(initialDate || '');
  const [halfDayStart, setHalfDayStart] = useState(false);
  const [halfDayEnd, setHalfDayEnd] = useState(false);
  const [selectedAbsenceTypeId, setSelectedAbsenceTypeId] = useState<string>('');
  const [employeeNote, setEmployeeNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Default-Typ: System-Urlaub, Fallback auf ersten verfügbaren Typ
  const defaultVacationTypeId = vacationTypes.find((t) => t.systemKey === 'VACATION')?.id ?? vacationTypes[0]?.id ?? '';

  // Im aktuellen Modus verfügbare Typen für das Dropdown
  const typesShown = mode === 'plan' ? typesForPlan : vacationTypes;

  // State zurücksetzen wenn Dialog (erneut) geöffnet wird
  useEffect(() => {
    if (open) {
      setStartDate(initialDate || '');
      setEndDate(initialDate || '');
      setHalfDayStart(false);
      setHalfDayEnd(false);
      setMode('add');
      setSelectedAbsenceTypeId(defaultVacationTypeId);
      setEmployeeNote('');
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [open, initialDate, defaultVacationTypeId]);

  // Validierung
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  const validationError = useMemo(() => {
    if (!startDate || !endDate) return null;
    if (startDate > endDate) return 'Enddatum muss nach dem Startdatum liegen.';
    if (startDate < yearStart || endDate > yearEnd)
      return `Datumsbereich muss im Jahr ${year} liegen.`;
    return null;
  }, [startDate, endDate, yearStart, yearEnd, year]);

  const hasValidDates = startDate && endDate && !validationError;
  const isSingleDay = startDate === endDate;

  // Bereich berechnen (Eintragen-Modus)
  const range = useMemo(() => {
    if (!hasValidDates) return [];
    return generateVacationRange(startDate, endDate, entries, holidays, {
      halfDayStart,
      halfDayEnd: isSingleDay ? false : halfDayEnd,
    });
  }, [hasValidDates, startDate, endDate, entries, holidays, halfDayStart, halfDayEnd, isSingleDay]);

  const summary = useMemo(() => calculateRangeSummary(range), [range]);
  const bookable = useMemo(() => getBookableDays(range), [range]);

  // Entfernen-Modus: Zähle entfernbare Tage
  const removableCount = useMemo(() => {
    if (!hasValidDates) return { fullDays: 0, halfDays: 0, total: 0 };
    return countRemovableDays(startDate, endDate, entries);
  }, [hasValidDates, startDate, endDate, entries]);

  // Ausgewählter Typ prüfen
  const selectedType = vacationTypes.find((t) => t.id === selectedAbsenceTypeId);
  const isApprovalRequired = selectedType?.requiresApproval === true && !!onSubmitRequest;

  const exceedsRemaining =
    mode === 'add' &&
    remainingVacation !== undefined &&
    summary.totalVacationDays > remainingVacation;

  const canSave =
    hasValidDates &&
    !submitting &&
    (mode === 'add'
      ? bookable.length > 0 && !exceedsRemaining
      : mode === 'plan'
        ? bookable.length > 0 && !!selectedAbsenceTypeId
        : removableCount.total > 0);

  const handleSave = async () => {
    if (!canSave) return;

    if (mode === 'plan' && onPlanRequest) {
      setSubmitting(true);
      setSubmitError(null);
      try {
        const data: CreateAbsenceRequestData = {
          absenceTypeId: selectedAbsenceTypeId,
          startDate,
          endDate,
          halfDayStart,
          halfDayEnd: isSingleDay ? false : halfDayEnd,
          employeeNote: employeeNote.trim() || null,
        };
        await onPlanRequest(data);
        onOpenChange(false);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Vormerken fehlgeschlagen';
        setSubmitError(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (mode === 'add' && isApprovalRequired) {
      // Antrag erstellen statt direkt eintragen
      setSubmitting(true);
      setSubmitError(null);
      try {
        const data: CreateAbsenceRequestData = {
          absenceTypeId: selectedAbsenceTypeId,
          startDate,
          endDate,
          halfDayStart,
          halfDayEnd: isSingleDay ? false : halfDayEnd,
          employeeNote: employeeNote.trim() || null,
        };
        await onSubmitRequest!(data);
        onOpenChange(false);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Antrag konnte nicht erstellt werden';
        setSubmitError(msg);
      } finally {
        setSubmitting(false);
      }
      return;
    }

    let updatedEntries: Record<string, DayEntry>;
    if (mode === 'add') {
      updatedEntries = createVacationEntries(bookable, entries, selectedAbsenceTypeId || undefined);
    } else {
      updatedEntries = removeVacationFromRange(startDate, endDate, entries);
    }

    onSave(updatedEntries);
    onOpenChange(false);
  };

  const daysLabel = summary.totalVacationDays !== 1 ? 'e' : '';
  const actionLabel =
    mode === 'add'
      ? isApprovalRequired
        ? `${summary.totalVacationDays} Tag${daysLabel} beantragen`
        : `${summary.totalVacationDays} Urlaubstag${daysLabel} eintragen`
      : mode === 'plan'
        ? `${summary.totalVacationDays} Tag${daysLabel} vormerken`
        : `${removableCount.total} Urlaubstag${removableCount.total !== 1 ? 'e' : ''} entfernen`;

  const title = employeeName
    ? `Urlaub für ${employeeName}`
    : 'Urlaubsblock';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-blue-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Modus-Umschalter */}
          <div className="flex gap-2 flex-wrap">
            <Button
              type="button"
              variant={mode === 'add' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('add')}
              className="flex-1 min-w-[120px]"
            >
              Urlaub eintragen
            </Button>
            {planEnabled && (
              <Button
                type="button"
                variant={mode === 'plan' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setMode('plan')}
                className="flex-1 min-w-[120px]"
              >
                <CalendarClock className="w-4 h-4 mr-1.5" />
                Vormerken
              </Button>
            )}
            <Button
              type="button"
              variant={mode === 'remove' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode('remove')}
              className="flex-1 min-w-[120px]"
            >
              Urlaub entfernen
            </Button>
          </div>

          {/* Abwesenheitsart-Auswahl (Eintragen + Vormerken, wenn mehrere Typen verfügbar) */}
          {(mode === 'add' || mode === 'plan') && typesShown.length > 1 && (
            <div>
              <Label className="text-sm font-medium">Abwesenheitsart</Label>
              <Select
                value={selectedAbsenceTypeId}
                onValueChange={setSelectedAbsenceTypeId}
              >
                <SelectTrigger className="w-full mt-1">
                  <SelectValue placeholder="Abwesenheitsart wählen" />
                </SelectTrigger>
                <SelectContent>
                  {typesShown.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <span className="flex items-center gap-2">
                        {type.icon && <span>{type.icon}</span>}
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Datumsauswahl */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vacation-start" className="text-sm font-medium">
                Startdatum
              </Label>
              <input
                id="vacation-start"
                type="date"
                value={startDate}
                min={yearStart}
                max={yearEnd}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  if (!endDate || e.target.value > endDate) {
                    setEndDate(e.target.value);
                  }
                }}
                className={cn(
                  'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                  'dark:bg-gray-800 dark:border-gray-600'
                )}
                aria-label="Startdatum"
              />
            </div>
            <div>
              <Label htmlFor="vacation-end" className="text-sm font-medium">
                Enddatum
              </Label>
              <input
                id="vacation-end"
                type="date"
                value={endDate}
                min={startDate || yearStart}
                max={yearEnd}
                onChange={(e) => setEndDate(e.target.value)}
                className={cn(
                  'mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
                  'dark:bg-gray-800 dark:border-gray-600'
                )}
                aria-label="Enddatum"
              />
            </div>
          </div>

          {/* Validierungsfehler */}
          {validationError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {validationError}
            </div>
          )}

          {/* Halber-Urlaub-Optionen (Eintragen + Vormerken) */}
          {(mode === 'add' || mode === 'plan') && hasValidDates && (
            <div className="space-y-2">
              {isSingleDay ? (
                <Label className="flex items-center gap-3 cursor-pointer">
                  <Checkbox
                    checked={halfDayStart}
                    onCheckedChange={(checked) => setHalfDayStart(checked as boolean)}
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">
                    Halber Urlaubstag
                  </span>
                </Label>
              ) : (
                <>
                  <Label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={halfDayStart}
                      onCheckedChange={(checked) => setHalfDayStart(checked as boolean)}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">
                      Erster Tag halber Urlaub
                    </span>
                  </Label>
                  <Label className="flex items-center gap-3 cursor-pointer">
                    <Checkbox
                      checked={halfDayEnd}
                      onCheckedChange={(checked) => setHalfDayEnd(checked as boolean)}
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-200">
                      Letzter Tag halber Urlaub
                    </span>
                  </Label>
                </>
              )}
            </div>
          )}

          {/* Vormerken-Hinweis */}
          {mode === 'plan' && hasValidDates && (
            <div className="flex items-center gap-2 p-3 rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <CalendarClock className="w-4 h-4 text-purple-500 flex-shrink-0" />
              <p className="text-xs text-purple-700 dark:text-purple-300">
                Tage werden vorgemerkt — keine Buchung, kein Antrag. Du kannst sie später einreichen oder buchen.
              </p>
            </div>
          )}

          {/* Genehmigungs-Hinweis + Notiz-Feld */}
          {mode === 'add' && isApprovalRequired && hasValidDates && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <Send className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Dieser Abwesenheitstyp erfordert eine Genehmigung. Der Antrag wird an deinen Vorgesetzten gesendet.
                </p>
              </div>
              <div>
                <Label htmlFor="employee-note" className="text-sm font-medium">
                  Anmerkung <span className="text-gray-400 font-normal">(optional)</span>
                </Label>
                <Textarea
                  id="employee-note"
                  value={employeeNote}
                  onChange={(e) => setEmployeeNote(e.target.value)}
                  placeholder="Grund oder Hinweis für den Vorgesetzten..."
                  maxLength={500}
                  className="mt-1 resize-none"
                  rows={2}
                />
              </div>
            </div>
          )}

          {/* Zusammenfassung */}
          {hasValidDates && (
            <Card className="bg-gray-50 dark:bg-gray-800/50">
              <CardContent className="p-4 space-y-3">
                {mode === 'add' || mode === 'plan' ? (
                  <>
                    {/* Eintragen/Vormerken-Zusammenfassung */}
                    <div className="flex items-center gap-2">
                      <Badge variant={mode === 'plan' ? 'purple' : 'info'}>
                        {summary.totalVacationDays} Tag{summary.totalVacationDays !== 1 ? 'e' : ''}
                      </Badge>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {mode === 'plan' ? 'werden vorgemerkt' : 'werden eingetragen'}
                      </span>
                    </div>

                    {summary.halfDays > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Davon {summary.halfDays} halbe{summary.halfDays !== 1 ? 'r' : ''} Tag{summary.halfDays !== 1 ? 'e' : ''}
                      </p>
                    )}

                    {summary.skippedDays > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {summary.skippedDays} Tag{summary.skippedDays !== 1 ? 'e' : ''} übersprungen
                        {summary.weekends > 0 && ` (${summary.weekends} Wochenende)`}
                        {summary.holidays > 0 && `, ${summary.holidays} Feiertag${summary.holidays !== 1 ? 'e' : ''}`}
                        {summary.sickDays > 0 && `, ${summary.sickDays} Krankheitstag${summary.sickDays !== 1 ? 'e' : ''}`}
                      </p>
                    )}

                    {summary.alreadyVacation > 0 && (
                      <p className="text-xs text-blue-600 dark:text-blue-400">
                        {summary.alreadyVacation} Tag{summary.alreadyVacation !== 1 ? 'e' : ''} bereits als Urlaub eingetragen (wird überschrieben)
                      </p>
                    )}

                    {mode === 'add' && remainingVacation !== undefined && totalEntitlement !== undefined && summary.totalVacationDays > 0 && (
                      <div className="pt-1 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs text-gray-600 dark:text-gray-300 tabular-nums">
                          → Danach {remainingVacation - summary.totalVacationDays} von {totalEntitlement} Tagen verbleibend
                        </p>
                        {exceedsRemaining ? (
                          <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1 mt-1">
                            <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                            Buchung überschreitet Ihren Urlaubsanspruch. Verfügbar: {remainingVacation} Tage. Gewünscht: {summary.totalVacationDays} Tage. Wenden Sie sich an die Personalabteilung.
                          </p>
                        ) : remainingVacation - summary.totalVacationDays === 0 ? (
                          <Badge variant="warning" className="mt-1">
                            Urlaubskonto wird vollständig aufgebraucht
                          </Badge>
                        ) : null}
                      </div>
                    )}

                    {bookable.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Keine buchbaren Tage im gewählten Zeitraum.
                      </p>
                    )}

                    {/* Hinweis bei Zukunftsbuchungen: Urlaubsstunden vorläufig */}
                    {mode === 'add' && startDate && new Date(startDate) > new Date() && bookable.length > 0 && (
                      <p className="text-xs text-blue-600 dark:text-blue-400 italic">
                        Urlaubsstunden werden vorläufig berechnet und aktualisieren sich automatisch, wenn weitere Arbeitszeiten erfasst werden.
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    {/* Entfernen-Zusammenfassung */}
                    {removableCount.total > 0 ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="warning">
                          {removableCount.total} Urlaubstag{removableCount.total !== 1 ? 'e' : ''}
                        </Badge>
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          werden entfernt
                        </span>
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Keine Urlaubstage im gewählten Zeitraum vorhanden.
                      </p>
                    )}

                    {removableCount.halfDays > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Davon {removableCount.halfDays} halbe{removableCount.halfDays !== 1 ? 'r' : ''} Tag{removableCount.halfDays !== 1 ? 'e' : ''}
                      </p>
                    )}
                  </>
                )}

                {/* Tagesliste */}
                {(mode === 'add' || mode === 'plan') && range.length > 0 && range.length <= 60 && (
                  <div className="max-h-[200px] overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md">
                    <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                      {range.map((day) => {
                        const dateObj = parseISO(day.date);
                        const label = format(dateObj, 'EEE, dd.MM.', { locale: de });

                        return (
                          <li
                            key={day.date}
                            className={cn(
                              'px-3 py-1.5 text-xs flex items-center justify-between',
                              day.willBeSkipped
                                ? 'text-gray-400 dark:text-gray-500 line-through'
                                : day.isAlreadyVacation
                                  ? 'text-blue-600 dark:text-blue-400'
                                  : 'text-green-700 dark:text-green-400'
                            )}
                          >
                            <span className="flex items-center gap-1.5">
                              {!day.willBeSkipped && (
                                <Check className="w-3 h-3 flex-shrink-0" />
                              )}
                              {day.willBeSkipped && (
                                <X className="w-3 h-3 flex-shrink-0" />
                              )}
                              {label}
                              {day.isHalfDay && (
                                <span className="text-blue-500 dark:text-blue-400">(½)</span>
                              )}
                            </span>
                            <span className="text-gray-400 dark:text-gray-500 text-[10px]">
                              {day.skipReason}
                              {!day.willBeSkipped && day.isAlreadyVacation && 'bereits Urlaub'}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {submitError && (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {submitError}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            variant={mode === 'remove' ? 'destructive' : 'default'}
            onClick={handleSave}
            disabled={!canSave}
          >
            {mode === 'add' && isApprovalRequired && canSave && <Send className="w-4 h-4 mr-1.5" />}
            {mode === 'plan' && canSave && <CalendarClock className="w-4 h-4 mr-1.5" />}
            {submitting
              ? 'Wird gesendet...'
              : canSave
                ? actionLabel
                : mode === 'remove'
                  ? 'Kein Urlaub vorhanden'
                  : 'Keine buchbaren Tage'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
